import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface WebsiteStoreProduct {
  id: string;
  slug: string;
  sku: string | null;
  title: string;
  short_description: string | null;
  description: string | null;
  product_type: 'physical' | 'digital';
  price_cents: number;
  currency: string;
  image_url: string | null;
  status: 'draft' | 'published' | 'archived';
  sort_order: number;
  allow_pickup: boolean;
  allow_shipping: boolean;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface WebsiteStoreSettings {
  id: number;
  storefront_title: string;
  storefront_blurb: string | null;
  pickup_enabled: boolean;
  pickup_label: string;
  pickup_instructions: string | null;
  flat_shipping_enabled: boolean;
  flat_shipping_cents: number;
  support_email: string | null;
  support_phone: string | null;
  updated_at: string;
}

export interface WebsiteStoreOrderItem {
  id: string;
  order_id: string;
  product_id: string | null;
  product_slug: string | null;
  product_title: string;
  product_type: 'physical' | 'digital';
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  image_url: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface WebsiteStoreOrder {
  id: string;
  user_id: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  status: 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded' | 'manual_review';
  fulfillment_status: 'pending' | 'processing' | 'ready_for_pickup' | 'fulfilled' | 'cancelled';
  fulfillment_method: 'pickup' | 'shipping' | 'digital';
  subtotal_cents: number;
  shipping_amount_cents: number;
  total_cents: number;
  currency: string;
  payment_provider: string;
  payment_status: string;
  payment_order_id: string | null;
  payment_capture_id: string | null;
  provider_checkout_id: string | null;
  payment_expires_at: string | null;
  paid_at: string | null;
  shipping_address: Record<string, unknown> | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  store_order_items?: WebsiteStoreOrderItem[];
}

const shouldDisplayWebsiteStoreOrder = (order: WebsiteStoreOrder) => {
  const paymentStatus = String(order.payment_status || '').trim().toLowerCase();
  const orderStatus = String(order.status || '').trim().toLowerCase();
  const visiblePaymentStatuses = new Set(['captured', 'completed', 'refunded']);
  const visibleOrderStatuses = new Set(['paid', 'fulfilled', 'refunded']);
  return visiblePaymentStatuses.has(paymentStatus) || visibleOrderStatuses.has(orderStatus);
};

export function useWebsiteStoreProducts() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['websiteStoreProducts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_products')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as WebsiteStoreProduct[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (product: Partial<WebsiteStoreProduct>) => {
      if (product.id) {
        const { data, error } = await supabase
          .from('store_products')
          .update(product)
          .eq('id', product.id)
          .select()
          .single();
        if (error) throw error;
        return data as WebsiteStoreProduct;
      }
      const { data, error } = await supabase
        .from('store_products')
        .insert(product)
        .select()
        .single();
      if (error) throw error;
      return data as WebsiteStoreProduct;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteStoreProducts'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('store_products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteStoreProducts'] }),
  });

  return {
    products: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    saveProduct: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    deleteProduct: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

export function useWebsiteStoreSettings() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['websiteStoreSettings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('store_settings').select('*').eq('id', 1).single();
      if (error) throw error;
      return data as WebsiteStoreSettings;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (settings: Partial<WebsiteStoreSettings>) => {
      const { data, error } = await supabase
        .from('store_settings')
        .update(settings)
        .eq('id', 1)
        .select()
        .single();
      if (error) throw error;
      return data as WebsiteStoreSettings;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteStoreSettings'] }),
  });

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    updateSettings: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}

export function useWebsiteStoreOrders() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['websiteStoreOrders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_orders')
        .select('*, store_order_items(*)')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return ((data ?? []) as WebsiteStoreOrder[]).filter(shouldDisplayWebsiteStoreOrder);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (args: {
      id: string;
      status?: WebsiteStoreOrder['status'];
      fulfillment_status?: WebsiteStoreOrder['fulfillment_status'];
      notes?: string | null;
    }) => {
      const { id, ...updates } = args;
      const { data, error } = await supabase
        .from('store_orders')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as WebsiteStoreOrder;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteStoreOrders'] }),
  });

  return {
    orders: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    updateOrder: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}